import { useEffect, useState } from "react";
import { Library, Search, GitBranch, X } from "lucide-react";
import { Header } from "@/pages/Operations";
import api from "@/lib/api";
import { AssetGrid, AssetCard } from "@/components/agent/AssetGrid";
import { UploadFootage } from "@/components/UploadFootage";

const KINDS = ["", "image", "video", "audio", "text", "web"];

export default function Assets() {
  const [workspaces, setWorkspaces] = useState([]);
  const [wsId, setWsId] = useState("");
  const [kind, setKind] = useState("");
  const [q, setQ] = useState("");
  const [data, setData] = useState({ assets: [], total: 0 });
  const [lineage, setLineage] = useState(null);

  const load = () => api.get("/assets", { params: { workspace_id: wsId || undefined, kind: kind || undefined, q: q || undefined, limit: 90 } }).then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { api.get("/workspaces").then((r) => setWorkspaces(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [wsId, kind]); // eslint-disable-line react-hooks/exhaustive-deps

  const showLineage = (a) => api.get(`/assets/${a.media_id}/lineage`).then((r) => setLineage(r.data));

  return (
    <div data-testid="assets-page">
      <Header icon={Library} eyebrow="Universal Asset Library" title="Every real output, in one place."
        subtitle="Generated, uploaded and edited media with provider, dimensions, workspace and lineage (source → edit → derivative). Nothing here is a placeholder — every card is a file on disk." />

      <div className="glass rounded-2xl p-4 flex flex-wrap items-center gap-3 mb-5">
        <select data-testid="assets-workspace-filter" value={wsId} onChange={(e) => setWsId(e.target.value)} className="bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-xs">
          <option value="">All workspaces</option>
          {workspaces.map((w) => <option key={w.workspace_id} value={w.workspace_id}>{w.name}</option>)}
        </select>
        <div className="flex gap-1">
          {KINDS.map((k) => <button key={k} data-testid={`assets-kind-${k || "all"}`} onClick={() => setKind(k)} className={`text-xs px-3 py-1.5 rounded-full border ${kind === k ? "border-purple-500/60 bg-purple-600/20 text-white" : "border-white/10 text-slate-400"}`}>{k || "all"}</button>)}
        </div>
        <form onSubmit={(e) => { e.preventDefault(); load(); }} className="flex items-center gap-2 flex-1 min-w-[200px]">
          <Search size={14} className="text-slate-500" />
          <input data-testid="assets-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search prompt / title / filename" className="flex-1 bg-transparent text-sm outline-none" />
        </form>
        <span data-testid="assets-total" className="text-xs text-slate-500 font-mono">{data.total} assets</span>
      </div>

      <div className="mb-5"><UploadFootage onUploaded={load} /></div>

      {data.assets.length === 0 ? (
        <div data-testid="assets-empty" className="glass rounded-2xl p-10 text-center text-slate-500 text-sm">No assets match. Generate something in the Studio or run the Executive AI.</div>
      ) : <AssetGrid assets={data.assets} onLineage={showLineage} />}

      {lineage && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-start justify-center p-6 overflow-y-auto" onClick={() => setLineage(null)}>
          <div data-testid="lineage-modal" className="glass rounded-2xl p-6 w-full max-w-4xl space-y-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <div className="inline-flex items-center gap-2 text-sm font-semibold"><GitBranch size={16} className="text-fuchsia-300" /> Lineage · {lineage.asset.media_id}</div>
              <button onClick={() => setLineage(null)} className="p-1 rounded-md hover:bg-white/10"><X size={16} /></button>
            </div>
            {lineage.ancestors.length > 0 && <section><div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-2">Source chain ({lineage.ancestors.length})</div><AssetGrid assets={lineage.ancestors} /></section>}
            <section><div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-2">This asset</div><div className="max-w-sm"><AssetCard a={lineage.asset} /></div></section>
            <section><div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-2">Derivatives ({lineage.derivatives.length})</div>
              {lineage.derivatives.length ? <AssetGrid assets={lineage.derivatives} /> : <div className="text-xs text-slate-500">No derivatives yet — use Edit tools to trim, reframe or add music.</div>}
            </section>
          </div>
        </div>
      )}
    </div>
  );
}
